import {
  AnalysisDecisionStatus,
  DecisionBannerIconType,
} from "@models/platform-analysis/enums/feature/decisionStatus";
import { theme } from "@themes";

export class FeeAnalysisPortfolioFeesConstants {
  static readonly Total_fee: string = "Total_fee";
  static readonly PDS_date: string = "PDS_date";
  static readonly Tm_fee_display: string = "Tm_fee_display";
  static readonly Tpr_fee_display: string = "Tpr_fee_display";
  static readonly Ttr_fee_display: string = "Ttr_fee_display";
  static readonly Managed_account_holdings: string = "Managed_account_holdings";
  static readonly Model_inception: string = "Model_inception";
  static readonly Model_min_inv: string = "Model_min_inv";
  static readonly Alloc_AU_cash: string = "Alloc_AU_cash";

  static readonly SummaryTableRowVariableNames: string[] = [
    this.Total_fee,
    this.PDS_date,
    this.Tm_fee_display,
    this.Tpr_fee_display,
    this.Ttr_fee_display,
    this.Managed_account_holdings,
  ];

  static readonly ExcludingPerformanceFeeVariableNames: string[] = [
    this.Tpr_fee_display,
  ];

  static readonly ShouldShowMultipleSectionVariableNames: string[] = [
    this.PDS_date,
    this.Model_inception,
    this.Model_min_inv,
    this.Alloc_AU_cash,
  ];
}

export class PortfolioFeesTableStyleConstants {
  static readonly TableHeaderHeight: number = 200;
  static readonly TableHeaderCollapsedHeight: number = 100;
  static readonly TableHeaderSmallPadding: number = 240;
  static readonly TableHeaderLargePadding: number = 270;
  static readonly MinimumSpaceToCollapse: number = 200;
}

export enum PortfolioFeesTableColumnSizeConstants {
  Small,
  Large,
}

export class HeatMapTableTitleConstants {
  static readonly DefaultTitle: string = "Annual platform fees (estimates)";
  static readonly DetailViewTitle: string =
    "Annual platform fees and SMA costs (estimates)";
  static readonly DetailViewExcPerfTitle: string =
    "Annual platform fees and SMA costs (estimates excluding performance fees)";
  static readonly ProjectionViewTitle: string =
    "Annual platform and SMA costs by portfolio value (estimates based on original portfolio allocation)";
  static readonly ProjectionViewExcPerfTitle: string =
    "Annual platform and SMA costs by portfolio value (estimates based on original portfolio allocation, excluding performance fees)";
}

export class SMAFeesColumnTooltipConstants {
  static readonly Title =
    "Total fees for the specified portfolio for 1-year (estimate)";
  static readonly Description =
    "<p>Estimated cost of holding model to the investor, calculated as the sum of Total management fees and costs, Total performance fees and Total transaction costs. Minimum holding sizes have not been factored into the calculations.</p><br><p>Past fees are not an indicator of future fees. The fees and costs breakdown are primarily based on a backward-looking allocation and performance of the model, and hence are likely to vary in the year ahead.</p>";
}

export class FeeAnalysisLabelConstants {
  static readonly TotalPlatformAndSMAFees: string = "Total platform & SMA fees";
  static readonly TotalFees: string = "Total fees";
  static readonly IDPSAdminAndAccountFees: string = "IDPS admin & account fees";
  static readonly SuperAdminAndAccountFees: string =
    "Super admin & account fees";
  static readonly InternationalAndOtherHoldingFees: string =
    "International & other holding fees";
  static readonly ExpenseRecoveryAndOtherFees: string =
    "Expense recovery & other fees";
  static readonly TransactionFeesNonSMA: string = "Transaction fees (non-SMA)";
  static readonly TransactionFees: string = "Transaction fees";
  static readonly SMAFees: string = "SMA fees";
  static readonly SMAFeesExcPerf: string = "<p>SMA fees</p><p>(exc. perf.)</p>";
  static readonly SMAFeesExcPerfPDF: string = "SMA fees\n(exc. perf.)";
  static readonly PYSRefund: string = "PYS refund";
  static readonly CashFees: string = "Cash fees";
  static readonly NetInterestEarnedOnCash: string =
    "Net interest earned on cash";
}

export class LineChartLegendConstants {
  static readonly YAxisPlatformFees: string =
    "Platform fees for the year ahead";
  static readonly YAxisPlatformAndSMAFees: string =
    "Platform and SMA fees for the year ahead";
  static readonly XAxis: string = "Average total portfolio value for the year";
}

export const DECISION_CONTAINER_BACKGROUND_CONFIG: Record<
  AnalysisDecisionStatus,
  string
> = {
  [AnalysisDecisionStatus.Disqualified]:
    theme.palette.decisionStatus.disqualified,
  [AnalysisDecisionStatus.Shortlisted]:
    theme.palette.decisionStatus.shortlisted,
  [AnalysisDecisionStatus.Undecided]: theme.palette.common.white,
  [AnalysisDecisionStatus.MissingEssential]:
    theme.palette.decisionStatus.missingEssential,
};

export const DECISION_BANNER_CONFIG: Record<
  AnalysisDecisionStatus,
  {
    backgroundColor: string;
    label: string;
    iconType?: DecisionBannerIconType;
  }
> = {
  [AnalysisDecisionStatus.Disqualified]: {
    backgroundColor: theme.palette.text.disabled,
    label: "Disqualified",
    iconType: DecisionBannerIconType.Cross,
  },
  [AnalysisDecisionStatus.Shortlisted]: {
    backgroundColor: theme.palette.success.light,
    label: "Added to shortlist",
    iconType: DecisionBannerIconType.Check,
  },
  [AnalysisDecisionStatus.Undecided]: {
    backgroundColor: "",
    label: "",
  },
  [AnalysisDecisionStatus.MissingEssential]: {
    backgroundColor: theme.palette.error[300],
    label: "Missing essential features",
    iconType: DecisionBannerIconType.TriangleWarning,
  },
};
